import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Mail, User, ShieldAlert, KeyRound } from "lucide-react";

export default function AdminUsers() {
  const { authFetch } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("admin");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch("https://api.elitecarephysiorehab.com/api/users");
      const result = await response.json();
      if (response.ok) {
        setUsers(result.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch admin users:", err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);

    try {
      const response = await authFetch("https://api.elitecarephysiorehab.com/api/users", {
        method: "POST",
        body: JSON.stringify({ name, email, password, role }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to create user");
      }

      setSuccess("Administrator user created successfully.");
      setName("");
      setEmail("");
      setPassword("");
      setRole("admin");
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <style>{`
        * { box-sizing: border-box; }
        .users-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 14px;
          flex-wrap: wrap;
          gap: 8px;
        }

        .users-header h2 {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }

        .users-grid {
          display: grid;
          grid-template-columns: 1.1fr 2fr;
          gap: 24px;
          align-items: start;
        }

        .user-form-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          padding: 20px;
        }

        .user-form-card h3 {
          margin: 0 0 14px;
          font-size: 17px;
          font-weight: 800;
          color: #0f172a;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          margin-bottom: 14px;
        }

        .form-label {
          font-size: 13px;
          font-weight: 800;
          color: #475569;
          margin-bottom: 5px;
        }

        .input-wrap {
          position: relative;
        }

        .input-icon {
          position: absolute;
          top: 50%;
          left: 12px;
          transform: translateY(-50%);
          color: #16a34a;
          width: 16px;
          height: 16px;
        }

        .users-input, .users-select {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          padding: 10px 12px 10px 38px;
          font-size: 14px;
          color: #0f172a;
          background: #fff;
          outline: none;
        }

        .users-select {
          padding-left: 12px;
        }

        .submit-btn {
          border: none;
          background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
          color: #fff;
          padding: 12px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 800;
          cursor: pointer;
          width: 100%;
        }

        .alert-box {
          border-radius: 12px;
          padding: 10px 12px;
          font-size: 13px;
          margin-bottom: 14px;
        }

        .alert-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #b91c1c;
        }

        .alert-success {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #15803d;
        }

        .users-table-container {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
        }

        .users-table-scroll {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .users-table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
          min-width: 560px;
        }

        .users-table th {
          background: #f8fafc;
          padding: 12px 16px;
          font-size: 12px;
          font-weight: 800;
          color: #64748b;
          border-bottom: 1px solid #e2e8f0;
        }

        .users-table td {
          padding: 12px 16px;
          font-size: 14px;
          border-bottom: 1px solid #e2e8f0;
          color: #0f172a;
        }

        .role-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 800;
        }

        .role-super {
          background: #fef3c7;
          color: #d97706;
        }

        .role-admin {
          background: #e0f2fe;
          color: #0284c7;
        }

        @media (max-width: 860px) {
          .users-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .users-header h2 {
            font-size: 18px;
          }
          .user-form-card {
            padding: 14px;
          }
        }
      `}</style>

      <div className="users-header">
        <h2>Admin Users</h2>
      </div>

      <div className="users-grid">
        <div className="user-form-card">
          <h3>Create New Admin</h3>

          {error && <div className="alert-box alert-error">{error}</div>}
          {success && <div className="alert-box alert-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <div className="input-wrap">
                <User className="input-icon" />
                <input
                  type="text"
                  className="users-input"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="input-wrap">
                <Mail className="input-icon" />
                <input
                  type="email"
                  className="users-input"
                  placeholder="john@elitecare.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Temporary Password</label>
              <div className="input-wrap">
                <KeyRound className="input-icon" />
                <input
                  type="password"
                  className="users-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="users-select"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="admin">Administrator</option>
                <option value="super_admin">Super Administrator</option>
              </select>
            </div>

            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? "Creating..." : "Add Admin User"}
            </button>
          </form>
        </div>

        <div className="users-table-container">
          <div className="users-table-scroll">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center", color: "#64748b" }}>
                      Loading users list...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center", color: "#64748b" }}>
                      No administrators found.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 700 }}>{u.name}</td>
                      <td>{u.email}</td>
                      <td>
                        {u.role === "super_admin" ? (
                          <span className="role-badge role-super">
                            <ShieldCheck size={10} />
                            Super Admin
                          </span>
                        ) : (
                          <span className="role-badge role-admin">
                            <ShieldAlert size={10} />
                            Admin
                          </span>
                        )}
                      </td>
                      <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
